import {
  EVENT_NAME_LENGTH_MAX,
  EVENT_PROPERTIES_MAX,
  EVENT_PROPERTY_NAME_MAX,
  EVENT_PROPERTY_VALUE_MAX,
} from '../constants';
import { outputErrorMessage } from './error-message';
import { errorTypes } from '../validators/error-types';
import { validateValue } from '../validators/base-validator';
import { TriggeredEvent } from '../triggered-event.types';
import { CustomEventNameNormalizer } from '../name-normalizer';

const flattenEventProperties = (properties: { [key: string]: any }) => {
  const flattenedObject: { [key: string]: string | number | null } = {};
  Object.keys(properties).forEach((key) => {
    if (properties[key] !== undefined) flattenedObject[key] = properties[key];
  });
  return flattenedObject;
};

// Validations ====================================================================================

const validateObjectCapacity = (name: string, properties: { [key: string]: any }): boolean => {
  if (Object.keys(properties).length > EVENT_PROPERTIES_MAX) {
    outputErrorMessage(errorTypes.CUSTOM, name, {
      custom: `Exceeded maximum number of properties. Please keep the number of properties within ${EVENT_PROPERTIES_MAX}.`,
    });
    return false;
  }
  return true;
};

const validateName = (name: string): boolean => {
  const [result, error] = validateValue(name, {
    type: 'string',
    denyStandardPrefix: true,
    denyEmptyString: true,
    maxStringLength: EVENT_NAME_LENGTH_MAX,
  });
  if (!result) {
    outputErrorMessage(error, name, {
      subject: `custom event name "${name}"`,
      maxStringLength: EVENT_NAME_LENGTH_MAX,
    });
    return false;
  }
  return true;
};

const validatePropertyKey = (name: string, key: string): boolean => {
  const [result, error] = validateValue(key, {
    type: 'string',
    denyEmptyString: true,
    maxStringLength: EVENT_PROPERTY_NAME_MAX,
  });
  if (!result) {
    outputErrorMessage(error, name, {
      subject: `custom property key "${key}"`,
      maxStringLength: EVENT_PROPERTY_NAME_MAX,
    });
    return false;
  }
  return true;
};

const validatePropertyValue = (name: string, key: string, properties: { [key: string]: any }): boolean => {
  const [result, error] = validateValue(properties[key], {
    allowNull: true,
    maxStringLength: EVENT_PROPERTY_VALUE_MAX,
  });
  if (!result) {
    outputErrorMessage(error, name, {
      subject: `value of property "${key}"`,
      maxStringLength: EVENT_PROPERTY_VALUE_MAX,
    });
    return false;
  }
  return true;
};

// Main ===========================================================================================

export const buildCustomEvent = (name: string, properties: { [key: string]: any }): TriggeredEvent | false => {
  const normalizedName = CustomEventNameNormalizer.process(name);

  if (typeof properties !== 'object' || properties instanceof Date) {
    outputErrorMessage(errorTypes.CUSTOM, normalizedName, {
      custom: `Properties must be declared in an object.`,
    });
    return false;
  }

  const flattenedProperties = flattenEventProperties(properties);
  if (
    !validateObjectCapacity(name, flattenedProperties) ||
    !validateName(normalizedName) ||
    !Object.keys(flattenedProperties).every(
      (key) =>
        validatePropertyKey(normalizedName, key) && validatePropertyValue(normalizedName, key, flattenedProperties)
    )
  ) {
    return false;
  }

  return {
    name: normalizedName,
    properties: flattenedProperties,
    tracked_at: new Date(),
    metadata: {},
  };
};
